export type CryptoToken = "USDT" | "USDC";

export type CryptoNetworkCode =
  | "ethereum"
  | "polygon"
  | "arbitrum"
  | "base"
  | "tron"
  | "solana";

export type CryptoAddressType = "evm" | "tron" | "solana";

export type CryptoTxType = "DEPOSIT" | "WITHDRAWAL" | "CHARGES" | "REVERSAL";
export type CryptoTxStatus = "CONFIRMED" | "PENDING" | "FAILED" | "FUNDING";

export interface CryptoNetworkInfo {
  code: CryptoNetworkCode;
  name: string;
  addressType: CryptoAddressType;
  requiresMemo?: boolean;
}

export const CRYPTO_NETWORKS: CryptoNetworkInfo[] = [
  { code: "ethereum", name: "Ethereum (ERC20)", addressType: "evm" },
  { code: "polygon", name: "Polygon", addressType: "evm" },
  { code: "arbitrum", name: "Arbitrum", addressType: "evm" },
  { code: "base", name: "Base", addressType: "evm" },
  { code: "tron", name: "Tron (TRC20)", addressType: "tron" },
  { code: "solana", name: "Solana", addressType: "solana" },
];

export const getNetworkInfo = (code: string): CryptoNetworkInfo =>
  CRYPTO_NETWORKS.find((n) => n.code === code) ?? {
    code: code as CryptoNetworkCode,
    name: code,
    addressType: "evm",
  };

/** One balance line from the API: a single token on a single network */
export interface CryptoBalanceEntry {
  network: CryptoNetworkCode;
  token: CryptoToken;
  balance: number;
  feeBalance: number;
  available: boolean;
}

/** There is exactly one Stable coin Wallet, holding multiple tokens across multiple networks */
export interface CryptoWallet {
  address: string; // shared EVM address (ethereum / polygon / arbitrum / base)
  tronAddress: string;
  solanaAddress: string;
  totalBalance: Record<CryptoToken, number>;
  balances: CryptoBalanceEntry[];
}

/** Returns the deposit address for a given network */
export const getAddressForNetwork = (wallet: CryptoWallet, network: CryptoNetworkCode): string => {
  const info = getNetworkInfo(network);
  if (info.addressType === "tron") return wallet.tronAddress;
  if (info.addressType === "solana") return wallet.solanaAddress;
  return wallet.address;
};

export interface CryptoTransaction {
  id: string;
  walletId: string;
  txHash: string | null;
  token: CryptoToken;
  network: CryptoNetworkCode;
  amount: number;
  fee: number;
  type: CryptoTxType;
  status: CryptoTxStatus;
  from: string;
  to: string;
  blockNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CryptoTransactionListResponse {
  data: CryptoTransaction[];
  pageCount: number;
  pageId: number;
  limit: number;
  totalCount: number;
}